import React, { useEffect, useState } from "react";
import Layout from "./components/Layout";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db, trackException } from "./services/firebase";
import { useNavigate } from "react-router-dom";
import {
    makeStyles, shorthands, Persona, Button, Table, TableBody,
    TableHeader, TableRow, TableHeaderCell, TableCell,
    TableCellLayout, Image, TableCellActions, Label
} from "@fluentui/react-components";
import { query, collection, getDocs, where } from "firebase/firestore";
import { EditFilled, EditRegular, bundleIcon, DeleteFilled, DeleteRegular } from "@fluentui/react-icons";
import NotificationDataService from "./services/notificationDataService";
import UserDataService from "./services/userDataService";
import moment from 'moment';
import DeleteEventDialog from "./components/DeleteEventDialog";
import NotificationForm from "./components/Forms/NotificationForm";
import debounce from 'lodash/debounce';
import CustomSearchInput from "./components/CustomSearchInput";
import { PlusSVG } from "./static/svgs";
import "./components/Forms/FormStyle.css";
import useBreakpoint from "./hooks/useBreakpoint";
import ExportCsvButtons from "./components/ExportCsvButtons";
const EditIcon = bundleIcon(EditFilled, EditRegular);
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular);

const useStyles = makeStyles({
    root: {
        ...shorthands.overflow("hidden"),
        display: "flex",
        height: "480px",
        backgroundColor: "#fff"
    },

    content: {
        ...shorthands.flex(1),
        ...shorthands.padding("16px"),
        display: "block",
        ...shorthands.gridArea("main")
    },

    breakFlex: {
        flexBasis: "100%",
        textAlign: "left"
    }
});

const columns = [
    { columnKey: "subject", label: "Subject" },
    { columnKey: "message", label: "Message" },
    { columnKey: "timestamp", label: "Time Sent" },
    { columnKey: "createdFor", label: "Sent To" }
];

function Notifications() {
    const styles = useStyles();
    const [user, loading, error] = useAuthState(auth);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [allusers, setAllUsers] = useState([]);
    const [selectedNotification, setSelectedNotification] = useState();
    const [allnotifications, setAllNotifications] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredNotifications, setFilteredNotifications] = useState([]);
    const [currentuserId, setCurrentuserId] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
    const breakFlexpoint = useBreakpoint();
    const isMobile = breakFlexpoint === 'mobile';
    const navigate = useNavigate();
    const openForm = (selectedItem) => {
        setIsFormOpen(true);
        setSelectedNotification(selectedItem);
    }

    const fetchUserNameAndNotifications = async () => {
        try {
            if (!!user) {
                const userquery = query(collection(db, "Users"), where("email", "==", user?.email));
                const userdoc = await getDocs(userquery);
                if (userdoc.docs.length > 0) {
                    const currentuser = userdoc.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
                    setCurrentuserId(currentuser[0].id);
                    const notificationsQuery = await NotificationDataService.getAllnotification();
                    let notificationsArr = notificationsQuery.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
                    setAllNotifications(notificationsArr);
                    setFilteredNotifications(notificationsArr);

                    const usersQuery = await UserDataService.getAllUser();
                    setAllUsers(usersQuery.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
                    //console.log(allevents);
                } else {
                    console.log('no access');
                }
            }
        } catch (err) {
            trackException(JSON.stringify(err))
            console.error(err);
            alert("An error occured while fetching user data");
        }
    };

    useEffect(() => {
        if (loading) return;
        if (!user) return navigate("/");
        fetchUserNameAndNotifications();
    }, [user, loading]);

    const openDeleteForm = (selectedItem) => {
        setIsDeleteDialogOpen(true);
        setSelectedNotification(selectedItem);
        // fetchUserNameAndEvents();
    }

    const DeleteNotification = async () => {
        NotificationDataService.deletenotification(selectedNotification.id);
        setIsDeleteDialogOpen(false);
        fetchUserNameAndNotifications();
    };

    // Debounce the handleSearch function
    const debouncedSearch = debounce(search => {
        const lowerSearch = search.toLowerCase();
        const filtered = allnotifications.filter(event => {
            return (
                event.subject.toLowerCase().includes(lowerSearch)
            )
        });
        setFilteredNotifications(filtered);
    }, 300); // Adjust the debounce delay as needed

    const handleSearch = event => {
        const value = event.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };
    const handleClear = () => {
        setSearchTerm('');
        debouncedSearch('');
    };

    // Sent To is a member name for a targeted message and "All" for a broadcast,
    // matching the rows.
    const toExportRow = (item) => {
      const recipient = allusers.find(t => t.id == item.createdFor) || {};
      return {
        "Subject": item?.subject ?? "",
        "Message": item?.message ?? "",
        "Time Sent": item?.createdDate ? moment(item.createdDate).format('Do MMMM YYYY hh mm A') : "",
        "Sent To": item?.createdFor
          ? `${recipient.firstName ?? ""} ${recipient.lastName ?? ""}`.trim()
          : "All",
      };
    };

    return (
        <Layout>
            <div className={styles.content}>
                <div className={"d-flex justify-space-between top-content"}>
                    <div className={"d-flex"} style={{ gap: 8 }}>
                        <Button icon={<PlusSVG />} className={"primary-btn"} shape="circular" appearance="primary" onClick={() => openForm(undefined)}>{'New Notification'.toUpperCase()}</Button>
                        <ExportCsvButtons
                            screenName="Notifications"
                            getFilteredRows={() => filteredNotifications}
                            getAllRows={() => allnotifications}
                            mapRow={toExportRow}
                        />
                    </div>
                    <CustomSearchInput
                        className={"custom-search-input"}
                        value={searchTerm}
                        onChange={handleSearch}
                        onClick={handleClear}
                    />
                </div>
                <div className={"table-layout"} style={{ border: isMobile ? "none" : undefined }} >
                    <Table aria-label="Table with cell actions">
                        <TableHeader className={"list-row"}>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableHeaderCell key={column.columnKey}>
                                        <span className={"listHeaderText"}>{column.label}</span>
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                       <TableBody>
  {filteredNotifications.length > 0 && filteredNotifications.map((item) => {
    const user = allusers.find(t => t.id == item.createdFor) || {};

    return isMobile ? (
      <div key={item.id} className="notification-card">
        <div className="notification-card-header">
          <div className="notification-card-title">{item.subject}</div>
          <div className="notification-card-actions">
            {/* Uncomment if you want edit button
            <Button
              icon={<EditIcon />}
              className="edit-icon"
              appearance="subtle"
              aria-label="Edit"
              onClick={() => openForm(item)}
            /> */}
            <Button
              icon={<DeleteIcon />}
              className="delete-icon"
              appearance="subtle"
              aria-label="Delete"
              onClick={() => openDeleteForm(item)}
            />
          </div>
        </div>
        <span className="separator"></span>
        <div className="notification-card-row">
          <span className="notification-card-label">Message</span>
          <span className="notification-card-value">{item.message.substring(0, 150)}</span>
        </div>
        <span className="separator"></span>
        <div className="notification-card-row">
          <span className="notification-card-label">Date</span>
          <span className="notification-card-value">{moment(item.createdDate).format('Do MMMM YYYY hh mm A')}</span>
        </div>
        <span className="separator"></span>
        <div className="notification-card-row">
          <span className="notification-card-label">For</span>
          <span className="notification-card-value">
            {!!item.createdFor && allusers.length > 0
              ? `${user.firstName || ''} ${user.lastName || ''}`
              : 'All'}
          </span>
        </div>
      </div>
    ) : (
      <TableRow key={item.id}>
        <TableCell className={"table-row-fonts__primary"}>
          <TableCellLayout>
            {item.subject}
          </TableCellLayout>
        </TableCell>
        <TableCell className={"table-row-fonts__desc"}>
          <TableCellLayout>
            <Label title={item.message}>{item.message.substring(0, 150)}</Label>
          </TableCellLayout>
        </TableCell>
        <TableCell className={"table-row-fonts__desc"}>
          <TableCellLayout>
            {moment(item.createdDate).format('Do MMMM YYYY hh mm A')}
          </TableCellLayout>
        </TableCell>
        <TableCell className={"table-row-fonts__desc"}>
          {!!item.createdFor && allusers.length > 0
            ? <Label>{user.firstName} {user.lastName}</Label>
            : <Label>All</Label>}
          <TableCellActions>
            {/* <Button
              icon={<EditIcon />}
              appearance="subtle"
              aria-label="Edit"
              onClick={() => openForm(item)}
            /> */}
            <Button
              icon={<DeleteIcon />}
              appearance="subtle"
              aria-label="Delete"
              onClick={() => openDeleteForm(item)}
            />
          </TableCellActions>
        </TableCell>
      </TableRow>
    );
  })}
</TableBody>
                    </Table>
                </div>
            </div>
            <NotificationForm isOpen={isFormOpen} setIsOpen={(val) => setIsFormOpen(val)} selectedNotification={selectedNotification}
                updateGrid={fetchUserNameAndNotifications} createdBy={currentuserId} options={allusers}></NotificationForm>
            <DeleteEventDialog isDeleteDialogOpen={isDeleteDialogOpen}
                setIsDeleteDialogOpen={setIsDeleteDialogOpen} DeleteEvent={DeleteNotification} text={'Notification'} />
        </Layout>
    )
}

export default Notifications;